import { Popover } from '@cowbell/ui/core';
import { faSparkles } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';
import type { Source, SourcesSummary } from '../../types/sources';
import styles from './SourcesButton.module.css';
import { SourcesPopover } from './SourcesPopover';

export interface SourcesButtonProps {
  readonly sources: Source[];
  /** Opaque counts when the assistant consulted sources but didn't inline
   *  `[N]` citations (e.g. a wide KB scan). Shown as the button label when
   *  `sources` is empty; ignored otherwise. */
  readonly summary?: SourcesSummary | null;
}

function summaryTotal(summary: SourcesSummary | null | undefined): number {
  if (!summary) return 0;
  return summary.kbDocs + summary.dataQueries + summary.other;
}

function buttonLabel(
  sourceCount: number,
  summary: SourcesSummary | null | undefined
): string {
  const count = sourceCount > 0 ? sourceCount : summaryTotal(summary);
  return `${count} Source${count === 1 ? '' : 's'}`;
}

/** Pill shown beneath an assistant message when sources are present.
 *  Click to open a popover listing each source; popover rows route through
 *  the host's `onSourceClick`. When only a `SourcesSummary` is present
 *  (counts without enumerable sources), the button still renders but the
 *  popover stays empty. */
export function SourcesButton({ sources, summary }: SourcesButtonProps) {
  const [opened, setOpened] = useState(false);
  const sourceCount = sources.length;
  const summaryCount = summaryTotal(summary);

  if (sourceCount === 0 && summaryCount === 0) return null;

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      width={360}
      position="top-start"
      shadow="md"
      withArrow
      zIndex={1300}
      floatingStrategy="fixed"
    >
      <Popover.Target>
        <button
          type="button"
          className={styles.sourcesButton}
          onClick={() => setOpened((o) => !o)}
          aria-label={buttonLabel(sourceCount, summary)}
        >
          <FontAwesomeIcon icon={faSparkles} style={{ fontSize: '0.7rem' }} />
          {buttonLabel(sourceCount, summary)}
        </button>
      </Popover.Target>
      <Popover.Dropdown>
        <SourcesPopover sources={sources} onClose={() => setOpened(false)} />
      </Popover.Dropdown>
    </Popover>
  );
}
